/**
 * This function is used to get the full name of an action.
 *
 * @param {string} name - The name of the action, expected to be in the format `module/action`.
 * @param {boolean} [withModuleName=false] - A boolean indicating whether the module name should be included in the returned string.
 *
 * @returns {string} If `withModuleName` is true, the function returns the `name` as it is. If `withModuleName` is false, the function returns the `name` without the module prefix.
 */
export const vuexName = (name: string, withModuleName = true): string => (withModuleName ? name : name.split("/").slice(1).join("/"));

export const defineVuexName = (name: string) => name.split("/").slice(1).join("/");

const createModuleActions = (module: string) => {
  return function createAction(action: string): string {
    return `${module}/${action}`;
  };
};

const createLessonAction = createModuleActions("lesson");
const createClassTeachingAction = createModuleActions("classTeaching");
const createStudentTeachingAction = createModuleActions("studentTeaching");
const createTeacherTeachingAction = createModuleActions("teacherTeaching");
const createTeacherAction = createModuleActions("teacher");
const createCallingAction = createModuleActions("calling");
const createTeacherRoomAction = createModuleActions("teacherRoom");
const createStudentRoomAction = createModuleActions("studentRoom");
const createRealtimeSettingsAction = createModuleActions("realtimeSettings");
const createSpacePresenceAction = createModuleActions("spacePresence");
const createAnnotationV2Action = createModuleActions("annotationV2");
const createModalAction = createModuleActions("modal");
const createDeviceAction = createModuleActions("device");
const createParentAction = createModuleActions("parent");
const createSpinAction = createModuleActions("spin");
const createWhiteBoardAction = createModuleActions("whiteboard");
const createTeamsAction = createModuleActions("teams");
const createAppAction = createModuleActions("app");
const createAuthAction = createModuleActions("auth");

export const MODULES = {
  LESSON: {
    DISPATCHES: {
      SET_INFO: createLessonAction("SET_INFO"),
      SET_BOTH_EXPOSURE_AND_SLIDE: createLessonAction("SET_BOTH_EXPOSURE_AND_SLIDE"),
      GET_ALTERNATE_MEDIA_URL: createLessonAction("GET_ALTERNATE_MEDIA_URL"),
      PROCESS_VISIBLE_TARGETS_DATA_FROM_API: createLessonAction("PROCESS_VISIBLE_TARGETS_DATA_FROM_API"),
      PROCESS_VISIBLE_TARGET_BY_FROM_MESSAGE: createLessonAction("PROCESS_VISIBLE_TARGET_BY_FROM_MESSAGE"),
      TOGGLE_VISIBLE_TARGET: createLessonAction("TOGGLE_VISIBLE_TARGET"),
      TOGGLE_ALL_VISIBLE_TARGETS: createLessonAction("TOGGLE_ALL_VISIBLE_TARGETS"),
    },
    COMMITS: {
      SET_EXPOSURES: createLessonAction("SET_EXPOSURES"),
      SET_CURRENT_EXPOSURE: createLessonAction("SET_CURRENT_EXPOSURE"),
      SET_CURRENT_EXPOSURE_ITEM_MEDIA: createLessonAction("SET_CURRENT_EXPOSURE_ITEM_MEDIA"),
      SET_EXPOSURE_STATUS: createLessonAction("SET_EXPOSURE_STATUS"),
      SET_TOTAL_TIME: createLessonAction("SET_TOTAL_TIME"),
      SET_PLAYED_TIME: createLessonAction("SET_PLAYED_TIME"),
      SET_PREVIOUS_EXPOSURE: createLessonAction("SET_PREVIOUS_EXPOSURE"),
      END_CURRENT_CONTENT: createLessonAction("END_CURRENT_CONTENT"),
      SET_PREVIOUS_EXPOSURE_ITEM_MEDIA: createLessonAction("SET_PREVIOUS_EXPOSURE_ITEM_MEDIA"),
      CLEAR_LESSON_DATA: createLessonAction("CLEAR_LESSON_DATA"),
      STORE_CACHE_IMAGE: createLessonAction("STORE_CACHE_IMAGE"),
      CLEAR_CACHE_IMAGE: createLessonAction("CLEAR_CACHE_IMAGE"),
      SET_ZOOM_RATIO: createLessonAction("SET_ZOOM_RATIO"),
      SET_IMG_COORDS: createLessonAction("SET_IMG_COORDS"),
      SET_LESSON_PREVIEW_OBJECTS: createLessonAction("SET_LESSON_PREVIEW_OBJECTS"),
      SET_SHOW_PREVIEW_CANVAS: createLessonAction("SET_SHOW_PREVIEW_CANVAS"),
      SET_ALTERNATE_MEDIA_URL: createLessonAction("SET_ALTERNATE_MEDIA_URL"),
      CLEAR_ZOOM_STATE: createLessonAction("CLEAR_ZOOM_STATE"),
      RESET_STATE: createLessonAction("RESET_STATE"),
      SET_PDF_SCROLL_PROGRESS: createLessonAction("SET_PDF_SCROLL_PROGRESS"),
      SET_VISIBLE_TARGET_TAGS: createLessonAction("SET_VISIBLE_TARGET_TAGS"),
      CLEAR_EXPOSURE: createLessonAction("CLEAR_EXPOSURE"),
    },
    GETTERS: {
      GET_CURRENT_EXPOSURE: createLessonAction("GET_CURRENT_EXPOSURE"),
      GET_NEXT_EXPOSURE: createLessonAction("GET_NEXT_EXPOSURE"),
      GET_CURRENT_EXPOSURE_ITEM_MEDIA: createLessonAction("GET_CURRENT_EXPOSURE_ITEM_MEDIA"),
      GET_CURRENT_EXPOSURE_TYPE: createLessonAction("GET_CURRENT_EXPOSURE_TYPE"),
      GET_NEXT_EXPOSURE_ITEM_MEDIA: createLessonAction("GET_NEXT_EXPOSURE_ITEM_MEDIA"),
      GET_PREV_EXPOSURE_ITEM_MEDIA: createLessonAction("GET_PREV_EXPOSURE_ITEM_MEDIA"),
      GET_EXPOSURES: createLessonAction("GET_EXPOSURES"),
      GET_ACTIVITY_STATISTIC: createLessonAction("GET_ACTIVITY_STATISTIC"),
      GET_PAGE: createLessonAction("GET_PAGE"),
      GET_REMAINING_TIME_STATISTIC: createLessonAction("GET_REMAINING_TIME_STATISTIC"),
      GET_PROGRESS_STATISTIC: createLessonAction("GET_PROGRESS_STATISTIC"),
      GET_IS_ALTERNATE_MEDIA_SHOWN: createLessonAction("GET_IS_ALTERNATE_MEDIA_SHOWN"),
      GET_PREVIOUS_EXPOSURE: createLessonAction("GET_PREVIOUS_EXPOSURE"),
      GET_PREVIOUS_EXPOSURE_ITEM_MEDIA: createLessonAction("GET_PREVIOUS_EXPOSURE_ITEM_MEDIA"),
      GET_FIND_CACHED_IMAGE: createLessonAction("GET_FIND_CACHED_IMAGE"),
      GET_ZOOM_RATIO: createLessonAction("GET_ZOOM_RATIO"),
      GET_IMG_COORDS: createLessonAction("GET_IMG_COORDS"),
      GET_PREVIEW_OBJECTS: createLessonAction("GET_PREVIEW_OBJECTS"),
      GET_IS_SHOW_PREVIEW_CANVAS: createLessonAction("GET_IS_SHOW_PREVIEW_CANVAS"),
      GET_LIST_READER_ITEMS_OF_CURRENT_EXPOSURE: createLessonAction("GET_LIST_READER_ITEMS_OF_CURRENT_EXPOSURE"),
      GET_PDF_SCROLL_PROGRESS: createLessonAction("GET_PDF_SCROLL_PROGRESS"),
      GET_ALTERNATIVE_SHOWN: createLessonAction("GET_ALTERNATIVE_SHOWN"),
      GET_VISIBLE_TARGET_TAGS: createLessonAction("GET_VISIBLE_TARGET_TAGS"),
    },
  },
  REALTIME_SETTINGS: {
    COMMITS: {
      SET_SETTINGS: createRealtimeSettingsAction("SET_SETTINGS"),
      RESET_STATE: createRealtimeSettingsAction("RESET_STATE"),
    },
    GETTERS: {
      GET_SETTINGS: createRealtimeSettingsAction("GET_SETTINGS"),
    },
  },
  SPACE_PRESENCE: {
    DISPATCHES: {
      INIT: createSpacePresenceAction("INIT"),
      UPDATE_SPACE: createSpacePresenceAction("UPDATE_SPACE"),
      REGISTER_HANDLERS: createSpacePresenceAction("REGISTER_HANDLERS"),
      SEND_CURSOR: createSpacePresenceAction("SEND_CURSOR"),
      SEND_LASER: createSpacePresenceAction("SEND_LASER"),
      SEND_PENCIL: createSpacePresenceAction("SEND_PENCIL"),
      UPDATE_PROFILE: createSpacePresenceAction("UPDATE_PROFILE"),
      DISCONNECT: createSpacePresenceAction("DISCONNECT"),
    },
    COMMITS: {
      SET_INSTANCE: createSpacePresenceAction("SET_INSTANCE"),
      SET_CHANNEL_NAME: createSpacePresenceAction("SET_CHANNEL_NAME"),
      SET_MEMBERS: createSpacePresenceAction("SET_MEMBERS"),
      SET_PROFILE_DATA: createSpacePresenceAction("SET_PROFILE_DATA"),
      RESET_STATE: createSpacePresenceAction("RESET_STATE"),
    },
    GETTERS: {
      GET_INSTANCE: createSpacePresenceAction("GET_INSTANCE"),
      GET_MEMBERS: createSpacePresenceAction("GET_MEMBERS"),
      GET_IS_INITIALIZED: createSpacePresenceAction("GET_IS_INITIALIZED"),
      GET_CHANNEL_NAME: createSpacePresenceAction("GET_CHANNEL_NAME"),
      GET_PROFILE_DATA: createSpacePresenceAction("GET_PROFILE_DATA"),
      GET_ONLINE_STATUS_BY_IDS: createSpacePresenceAction("GET_ONLINE_STATUS_BY_IDS"),
      GET_STUDENT_USER_IDS: createSpacePresenceAction("GET_STUDENT_USER_IDS"),
    },
  },
  TEACHER_ROOM: {
    DISPATCHES: {
      END_CLASS: createTeacherRoomAction("END_CLASS"),
      RESET_CLASS: createTeacherRoomAction("RESET_CLASS"),
      HELPER_EXIT_CLASS: createTeacherRoomAction("HELPER_EXIT_CLASS"),
      SET_TEACHING_MODE_ASYNC: createTeacherRoomAction("SET_TEACHING_MODE_ASYNC"),
      MANAGE_REMOTE_USER_VIDEO_AND_AUDIO_SUBSCRIPTIONS: createTeacherRoomAction("MANAGE_REMOTE_USER_VIDEO_AND_AUDIO_SUBSCRIPTIONS"),
      LEAVE_ROOM: createTeacherRoomAction("LEAVE_ROOM"),
      ENTER_CLASS: createTeacherRoomAction("ENTER_CLASS"),
      JOIN_ROOM: createTeacherRoomAction("JOIN_ROOM"),
      UP_TO_DATE_SESSION_AFTER_RECONNECTED: createTeacherRoomAction("UP_TO_DATE_SESSION_AFTER_RECONNECTED"),
      INIT_CLASS_ROOM: createTeacherRoomAction("INIT_CLASS_ROOM"),
      FORCE_OUT_HELPER_ONE_TO_ONE: createTeacherRoomAction("FORCE_OUT_HELPER_ONE_TO_ONE"),
      SET_SPEAKING_USERS: createTeacherRoomAction("SET_SPEAKING_USERS"),
      SET_TEACHER_AUDIO: createTeacherRoomAction("SET_TEACHER_AUDIO"),
      SET_TEACHER_VIDEO: createTeacherRoomAction("SET_TEACHER_VIDEO"),
      END_EXPOSURE: createTeacherRoomAction("END_EXPOSURE"),
      REQUEST_CHANGE_EXPOSURE_ITEM: createTeacherRoomAction("REQUEST_CHANGE_EXPOSURE_ITEM"),
      SET_CLASS_ACTION: createTeacherRoomAction("SET_CLASS_ACTION"),
      SET_ANNOTATION_MODE: createTeacherRoomAction("SET_ANNOTATION_MODE"),
      SEND_LAST_LINE_ASYNC: createTeacherRoomAction("SEND_LAST_LINE_ASYNC"),
      REQUEST_CLEAR_BOARD_ASYNC: createTeacherRoomAction("REQUEST_CLEAR_BOARD_ASYNC"),
      UPDATE_ZOOM_SLIDE: createTeacherRoomAction("UPDATE_ZOOM_SLIDE"),
      SET_MOVE_ZOOMED_SLIDE: createTeacherRoomAction("SET_MOVE_ZOOMED_SLIDE"),
      SEND_ONE_AND_ONE: createTeacherRoomAction("SEND_ONE_AND_ONE"),
      SET_WHITEBOARD: createTeacherRoomAction("SET_WHITEBOARD"),
      SET_MEDIA_STATE: createTeacherRoomAction("SET_MEDIA_STATE"),
      SET_LASER_PATH: createTeacherRoomAction("SET_LASER_PATH"),
      SET_ONLINE: createTeacherRoomAction("SET_ONLINE"),
      SET_OFFLINE: createTeacherRoomAction("SET_OFFLINE"),
      SET_SHAPES_FOR_STUDENT: createTeacherRoomAction("SET_SHAPES_FOR_STUDENT"),
      GET_AVATAR_TEACHER: createTeacherRoomAction("GET_AVATAR_TEACHER"),
      GET_AVATAR_HELPER: createTeacherRoomAction("GET_AVATAR_HELPER"),
      TEACHER_MODIFY_FABRIC_OBJECT: createTeacherRoomAction("TEACHER_MODIFY_FABRIC_OBJECT"),
      SET_LESSON_AND_UNIT: createTeacherRoomAction("SET_LESSON_AND_UNIT"),
      SET_PENCIL_PATH: createTeacherRoomAction("SET_PENCIL_PATH"),
      TEACHER_REMOVE_HELPER: createTeacherRoomAction("TEACHER_REMOVE_HELPER"),
      TOGGLE_HELPER_MICRO: createTeacherRoomAction("TOGGLE_HELPER_MICRO"),
      TOGGLE_HELPER_CAMERA: createTeacherRoomAction("TOGGLE_HELPER_CAMERA"),
      CHANGE_LOCAL_EXPOSURE_ITEM: createTeacherRoomAction("CHANGE_LOCAL_EXPOSURE_ITEM"),
      TOGGLE_ONE_TO_ONE_WITH_HELPER_IGNORE_TEACHER_VOICE: createTeacherRoomAction("TOGGLE_ONE_TO_ONE_WITH_HELPER_IGNORE_TEACHER_VOICE"),
      UPDATE_REMOTE_PDF_SCROLL_PROGRESS: createTeacherRoomAction("UPDATE_REMOTE_PDF_SCROLL_PROGRESS"),
      REQUEST_UPDATE_CANVAS_OBJECT_ASYNC: createTeacherRoomAction("REQUEST_UPDATE_CANVAS_OBJECT_ASYNC"),
      BROADCAST_MESSAGE_UPDATE_CANVAS_OBJECT: createTeacherRoomAction("BROADCAST_MESSAGE_UPDATE_CANVAS_OBJECT"),
      TOGGLE_STUDENT_MEDIA_DEVICES_ASYNC: createTeacherRoomAction("TOGGLE_STUDENT_MEDIA_DEVICES_ASYNC"),
      TOGGLE_STUDENT_PALETTE_ASYNC: createTeacherRoomAction("TOGGLE_STUDENT_PALETTE_ASYNC"),
      SET_STUDENTS_BADGE_ASYNC: createTeacherRoomAction("SET_STUDENTS_BADGE_ASYNC"),
      CLEAR_STUDENT_RAISING_HAND_ASYNC: createTeacherRoomAction("CLEAR_STUDENT_RAISING_HAND_ASYNC"),
      HANDLE_REMOTE_USER_JOINED: createTeacherRoomAction("HANDLE_REMOTE_USER_JOINED"),
      TEACHER_UPDATE_TEXTBOX: createTeacherRoomAction("TEACHER_UPDATE_TEXTBOX"),
    },
    COMMITS: {
      END_CLASS: createTeacherRoomAction("END_CLASS"),
      LEAVE_CLASS: createTeacherRoomAction("LEAVE_CLASS"),
      SET_TEACHING_MODE: createTeacherRoomAction("SET_TEACHING_MODE"),
      SET_ERROR: createTeacherRoomAction("SET_ERROR"),
      SET_USER: createTeacherRoomAction("SET_USER"),
      SET_ROOM_INFO: createTeacherRoomAction("SET_ROOM_INFO"),
      SET_STUDENTS_BADGE: createTeacherRoomAction("SET_STUDENTS_BADGE"),
      // SET_STUDENT_CONNECTION_STATUS: createTeacherRoomAction("SET_STUDENT_CONNECTION_STATUS"),
      UPDATE_RAISING_HAND: createTeacherRoomAction("UPDATE_RAISING_HAND"),
      UPDATE_IS_PALETTE: createTeacherRoomAction("UPDATE_IS_PALETTE"),
      SET_STUDENT_LEFT_CLASS: createTeacherRoomAction("SET_STUDENT_LEFT_CLASS"),
      SET_STUDENT_RAISING_HAND: createTeacherRoomAction("SET_STUDENT_RAISING_HAND"),
      SET_CLASS_ACTION: createTeacherRoomAction("SET_CLASS_ACTION"),
      SET_STUDENT_ONE_ID: createTeacherRoomAction("SET_STUDENT_ONE_ID"),
      SET_STUDENT_ONE_WITH_ANOTHER_TEACHER_ID: createTeacherRoomAction("SET_STUDENT_ONE_WITH_ANOTHER_TEACHER_ID"),
      SET_SPEAKING_USERS: createTeacherRoomAction("SET_SPEAKING_USERS"),
      SET_STUDENT_PALETTE: createTeacherRoomAction("SET_STUDENT_PALETTE"),
      SET_ONLINE: createTeacherRoomAction("SET_ONLINE"),
      SET_OFFLINE: createTeacherRoomAction("SET_OFFLINE"),
      SET_LIST_STUDENT_LOW_BAND_WIDTH: createTeacherRoomAction("SET_LIST_STUDENT_LOW_BAND_WIDTH"),
      SET_WHITEBOARD: createTeacherRoomAction("SET_WHITEBOARD"),
      SET_AVATAR_TEACHER: createTeacherRoomAction("SET_AVATAR_TEACHER"),
      SET_AVATAR_HELPER: createTeacherRoomAction("SET_AVATAR_HELPER"),
      SET_TEACHER_DISCONNECTED: createTeacherRoomAction("SET_TEACHER_DISCONNECTED"),
      SET_IS_TEACHER_USE_ONLY: createTeacherRoomAction("SET_IS_TEACHER_USE_ONLY"),
      SET_HELPER_INFO: createTeacherRoomAction("SET_HELPER_INFO"),
      SET_HELPER_VIDEO_STATUS: createTeacherRoomAction("SET_HELPER_VIDEO_STATUS"),
      SET_PARAMS_FOR_HELPER_JOIN_SESSION: createTeacherRoomAction("SET_PARAMS_FOR_HELPER_JOIN_SESSION"),
      MAKE_HELPER_THE_NEW_TEACHER: createTeacherRoomAction("MAKE_HELPER_THE_NEW_TEACHER"),
      SET_IS_SESSION_ENDED: createTeacherRoomAction("SET_IS_SESSION_ENDED"),
      SET_INITIAL_TEACHER_DISCONNECT_STATUS: createTeacherRoomAction("SET_INITIAL_TEACHER_DISCONNECT_STATUS"),
      SET_IS_CONNECTED: createTeacherRoomAction("SET_IS_CONNECTED"),
      SET_IS_REALTIME_READY: createTeacherRoomAction("SET_IS_REALTIME_READY"),
    },
    GETTERS: {
      GET_IS_SOCKET_CONNECTED: createTeacherRoomAction("GET_IS_SOCKET_CONNECTED"),
      GET_IS_REALTIME_READY: createTeacherRoomAction("GET_IS_REALTIME_READY"),
      GET_CAN_SEND_REALTIME_ACTION: createTeacherRoomAction("GET_CAN_SEND_REALTIME_ACTION"),
      GET_INFO: createTeacherRoomAction("GET_INFO"),
      GET_ERROR: createTeacherRoomAction("GET_ERROR"),
      GET_STUDENTS: createTeacherRoomAction("GET_STUDENTS"),
      GET_TEACHER: createTeacherRoomAction("GET_TEACHER"),
      GET_ROOM_MANAGER: createTeacherRoomAction("GET_ROOM_MANAGER"),
      GET_TEACHING_MODE: createTeacherRoomAction("GET_TEACHING_MODE"),
      GET_CLASS_ACTION: createTeacherRoomAction("GET_CLASS_ACTION"),
      GET_STUDENT_MODE_ONE_ID: createTeacherRoomAction("GET_STUDENT_MODE_ONE_ID"),
      GET_STUDENT_MODE_ONE_WITH_ANOTHER_TEACHER_ID: createTeacherRoomAction("GET_STUDENT_MODE_ONE_WITH_ANOTHER_TEACHER_ID"),
      GET_TEACHER_OR_HELPER_ONE_TO_ONE: createTeacherRoomAction("GET_TEACHER_OR_HELPER_ONE_TO_ONE"),
      GET_STUDENT_MODE_ONE_WITH_ANOTHER_TEACHER: createTeacherRoomAction("GET_STUDENT_MODE_ONE_WITH_ANOTHER_TEACHER"),
      GET_ONE_TO_ONE_STUDENT_IDS: createTeacherRoomAction("GET_ONE_TO_ONE_STUDENT_IDS"),
      GET_NOT_ONE_TO_ONE_STUDENT_IDS: createTeacherRoomAction("GET_NOT_ONE_TO_ONE_STUDENT_IDS"),
      GET_SPEAKING_USERS: createTeacherRoomAction("GET_SPEAKING_USERS"),
      GET_IS_DISCONNECTED: createTeacherRoomAction("GET_IS_DISCONNECTED"),
      GET_LIST_STUDENT_LOW_BAND_WIDTH: createTeacherRoomAction("GET_LIST_STUDENT_LOW_BAND_WIDTH"),
      GET_CURRENT_LESSON: createTeacherRoomAction("GET_CURRENT_LESSON"),
      GET_CURRENT_UNIT: createTeacherRoomAction("GET_CURRENT_UNIT"),
      GET_IS_SHOW_WHITE_BOARD: createTeacherRoomAction("GET_IS_SHOW_WHITE_BOARD"),
      GET_IS_TEACHER_USE_ONLY: createTeacherRoomAction("GET_IS_TEACHER_USE_ONLY"),
      GET_MEDIA_STATE: createTeacherRoomAction("GET_MEDIA_STATE"),
      GET_HELPER_ID: createTeacherRoomAction("GET_HELPER_ID"),
      GET_HELPER_INFO: createTeacherRoomAction("GET_HELPER_INFO"),
      GET_HELPER_AVATAR: createTeacherRoomAction("GET_HELPER_AVATAR"),
      GET_PARAMS_TO_JOIN_CUR_SESSION: createTeacherRoomAction("GET_PARAMS_TO_JOIN_CUR_SESSION"),
      GET_TEACHER_IS_DISCONNECTED: createTeacherRoomAction("GET_TEACHER_IS_DISCONNECTED"),
      GET_STUDENT_IDS_IN_ONE_TO_ONE_MODE: createTeacherRoomAction("GET_STUDENT_IDS_IN_ONE_TO_ONE_MODE"),
      GET_ONE_TO_ONE_STUDENT_ID: createTeacherRoomAction("GET_ONE_TO_ONE_STUDENT_ID"),
      GET_HELPER_ONE_TO_ONE_STUDENT_ID: createTeacherRoomAction("GET_HELPER_ONE_TO_ONE_STUDENT_ID"),
      GET_IS_TEACHER_ALLOW_STUDENT_SEE_HELPER_VIDEO: createTeacherRoomAction("GET_IS_TEACHER_ALLOW_STUDENT_SEE_HELPER_VIDEO"),
    },
  },
  STUDENT_ROOM: {
    DISPATCHES: {
      GET_CLASS_ROOM_INFO: createStudentRoomAction("GET_CLASS_ROOM_INFO"),
      INIT_CLASS_ROOM: createStudentRoomAction("INIT_CLASS_ROOM"),
      MANAGE_REMOTE_USER_VIDEO_AND_AUDIO_SUBSCRIPTIONS: createStudentRoomAction("MANAGE_REMOTE_USER_VIDEO_AND_AUDIO_SUBSCRIPTIONS"),
      ENTER_CLASS: createStudentRoomAction("ENTER_CLASS"),
      JOIN_ROOM: createStudentRoomAction("JOIN_ROOM"),
      SET_SPEAKING_USERS: createStudentRoomAction("SET_SPEAKING_USERS"),
      LEAVE_ROOM: createStudentRoomAction("LEAVE_ROOM"),
      SET_STUDENT_AUDIO: createStudentRoomAction("SET_STUDENT_AUDIO"),
      TOGGLE_CURRENT_STUDENT_MICROPHONE: createStudentRoomAction("TOGGLE_CURRENT_STUDENT_MICROPHONE"),
      SET_STUDENT_VIDEO: createStudentRoomAction("SET_STUDENT_VIDEO"),
      TOGGLE_CURRENT_STUDENT_CAMERA: createStudentRoomAction("TOGGLE_CURRENT_STUDENT_CAMERA"),
      STUDENT_RAISING_HAND: createStudentRoomAction("STUDENT_RAISING_HAND"),
      SET_ONLINE: createStudentRoomAction("SET_ONLINE"),
      SET_OFFLINE: createStudentRoomAction("SET_OFFLINE"),
      STUDENT_LEAVE_CLASS: createStudentRoomAction("STUDENT_LEAVE_CLASS"),
      GET_AVATAR_TEACHER: createStudentRoomAction("GET_AVATAR_TEACHER"),
      GET_AVATAR_HELPER: createStudentRoomAction("GET_AVATAR_HELPER"),
      SET_ROOM_INFO: createStudentRoomAction("SET_ROOM_INFO"),
      SET_CLASS_DATA: createStudentRoomAction("SET_CLASS_DATA"),
      SET_API_ERROR: createStudentRoomAction("SET_API_ERROR"),
      CHANGE_INDEPENDENT_ITEM: createStudentRoomAction("CHANGE_INDEPENDENT_ITEM"),
      UP_TO_DATE_SESSION_AFTER_RECONNECTED: createStudentRoomAction("UP_TO_DATE_SESSION_AFTER_RECONNECTED"),
      HANDLE_TEACHER_TOGGLE_STUDENT_MEDIA_DEVICES: createStudentRoomAction("HANDLE_TEACHER_TOGGLE_STUDENT_MEDIA_DEVICES"),
      HANDLE_REMOTE_USER_JOINED: createStudentRoomAction("HANDLE_REMOTE_USER_JOINED"),
    },
    COMMITS: {
      SET_INIT_CLASS_LOADING: createStudentRoomAction("SET_INIT_CLASS_LOADING"),
      SET_IS_SESSION_ENDED: createStudentRoomAction("SET_IS_SESSION_ENDED"),
      SET_IS_CONNECTED: createStudentRoomAction("SET_IS_CONNECTED"),
      LEAVE_ROOM: createStudentRoomAction("LEAVE_ROOM"),
      SET_API_STATUS: createStudentRoomAction("SET_API_STATUS"),
      SET_USER: createStudentRoomAction("SET_USER"),
      SET_ROOM_INFO: createStudentRoomAction("SET_ROOM_INFO"),
      // SET_STUDENT_CONNECTION_STATUS: createStudentRoomAction("SET_STUDENT_CONNECTION_STATUS"),
      UPDATE_RAISING_HAND: createStudentRoomAction("UPDATE_RAISING_HAND"),
      UPDATE_IS_PALETTE: createStudentRoomAction("UPDATE_IS_PALETTE"),
      CLEAR_CIRCLE_STATUS: createStudentRoomAction("CLEAR_CIRCLE_STATUS"),
      SET_STUDENTS_BADGE: createStudentRoomAction("SET_STUDENTS_BADGE"),
      // SET_TEACHER_CONNECTION_STATUS: createStudentRoomAction("SET_TEACHER_CONNECTION_STATUS"),
      SET_STUDENT_PALETTE: createStudentRoomAction("SET_STUDENT_PALETTE"),
      SET_CLASS_ACTION: createStudentRoomAction("SET_CLASS_ACTION"),
      SET_STUDENT_ONE_ID: createStudentRoomAction("SET_STUDENT_ONE_ID"),
      SET_SPEAKING_USERS: createStudentRoomAction("SET_SPEAKING_USERS"),
      SET_STUDENT_RAISING_HAND: createStudentRoomAction("SET_STUDENT_RAISING_HAND"),
      SET_WHITEBOARD: createStudentRoomAction("SET_WHITEBOARD"),
      SET_ONLINE: createStudentRoomAction("SET_ONLINE"),
      SET_OFFLINE: createStudentRoomAction("SET_OFFLINE"),
      SET_IS_JOINED: createStudentRoomAction("SET_IS_JOINED"),
      SET_TEACHER_DISCONNECTED: createStudentRoomAction("SET_TEACHER_DISCONNECTED"),
      SET_AVATAR_TEACHER: createStudentRoomAction("SET_AVATAR_TEACHER"),
      SET_AVATAR_HELPER: createStudentRoomAction("SET_AVATAR_HELPER"),
      TOGGLE_VIDEOS_FEED: createStudentRoomAction("TOGGLE_VIDEOS_FEED"),
      SET_HELPER_INFO: createStudentRoomAction("SET_HELPER_INFO"),
      SET_HELPER_VIDEO_STATUS: createStudentRoomAction("SET_HELPER_VIDEO_STATUS"),
      MAKE_HELPER_THE_NEW_TEACHER: createStudentRoomAction("MAKE_HELPER_THE_NEW_TEACHER"),
    },
    GETTERS: {
      GET_INIT_CLASS_LOADING: createStudentRoomAction("GET_INIT_CLASS_LOADING"),
      GET_IS_SOCKET_CONNECTED: createStudentRoomAction("GET_IS_SOCKET_CONNECTED"),
      GET_IS_AGORA_CLIENT_INITIALIZED: createStudentRoomAction("GET_IS_AGORA_CLIENT_INITIALIZED"),
      GET_API_STATUS: createStudentRoomAction("GET_API_STATUS"),
      GET_INFO: createStudentRoomAction("GET_INFO"),
      GET_CLASS_INFO: createStudentRoomAction("GET_CLASS_INFO"),
      GET_CLASSES: createStudentRoomAction("GET_CLASSES"),
      GET_STUDENTS: createStudentRoomAction("GET_STUDENTS"),
      GET_TEACHER: createStudentRoomAction("GET_TEACHER"),
      GET_STUDENT: createStudentRoomAction("GET_STUDENT"),
      GET_LOCAL_STUDENT_ID: createStudentRoomAction("GET_LOCAL_STUDENT_ID"),
      GET_CLASS_ACTION: createStudentRoomAction("GET_CLASS_ACTION"),
      GET_STUDENT_MODE_ONE_ID: createStudentRoomAction("GET_STUDENT_MODE_ONE_ID"),
      GET_STUDENT_MODE_HELPER_ONE_ID: createStudentRoomAction("GET_STUDENT_MODE_HELPER_ONE_ID"),
      GET_ONE_TO_ONE_STUDENT_IDS: createStudentRoomAction("GET_ONE_TO_ONE_STUDENT_IDS"),
      GET_NOT_ONE_TO_ONE_STUDENT_IDS: createStudentRoomAction("GET_NOT_ONE_TO_ONE_STUDENT_IDS"),
      GET_SPEAKING_USERS: createStudentRoomAction("GET_SPEAKING_USERS"),
      GET_IS_SHOW_WHITEBOARD: createStudentRoomAction("GET_IS_SHOW_WHITEBOARD"),
      GET_IS_DISCONNECTED: createStudentRoomAction("GET_IS_DISCONNECTED"),
      GET_TEACHER_IS_DISCONNECTED: createStudentRoomAction("GET_TEACHER_IS_DISCONNECTED"),
      GET_VIDEOS_FEED_VISIBLE: createStudentRoomAction("GET_VIDEOS_FEED_VISIBLE"),
      GET_USER: createStudentRoomAction("GET_USER"),
      GET_IS_LOGGED_IN_AS_STUDENT: createStudentRoomAction("GET_IS_LOGGED_IN_AS_STUDENT"),
      GET_MEDIA_STATE: createStudentRoomAction("GET_MEDIA_STATE"),
      GET_HELPER_INFO: createStudentRoomAction("GET_HELPER_INFO"),
      GET_HELPER_ID: createStudentRoomAction("GET_HELPER_ID"),
      GET_HELPER_VIDEO_STATUS: createStudentRoomAction("GET_HELPER_VIDEO_STATUS"),
      GET_HELPER_AVATAR: createStudentRoomAction("GET_HELPER_AVATAR"),
      GET_IS_ME_IN_ONE_ONE: createStudentRoomAction("GET_IS_ME_IN_ONE_ONE"),
      GET_IS_IN_ONE_TO_ONE_MODE_WITH_HELPER: createStudentRoomAction("GET_IS_IN_ONE_TO_ONE_MODE_WITH_HELPER"),
      GET_IS_IN_ONE_TO_ONE_MODE_WITH_TEACHER: createStudentRoomAction("GET_IS_IN_ONE_TO_ONE_MODE_WITH_TEACHER"),
      GET_IS_OTHER_STUDENT_IN_ONE_TO_ONE_WITH_TEACHER: createStudentRoomAction("GET_IS_OTHER_STUDENT_IN_ONE_TO_ONE_WITH_TEACHER"),
      GET_IS_OTHER_STUDENT_IN_ONE_TO_ONE_WITH_HELPER: createStudentRoomAction("GET_IS_OTHER_STUDENT_IN_ONE_TO_ONE_WITH_HELPER"),
      GET_IS_LOCAL_STUDENT_RAISED_HAND: createStudentRoomAction("GET_IS_LOCAL_STUDENT_RAISED_HAND"),
      GET_ONE_TO_ONE_STUDENT_ID: createStudentRoomAction("GET_ONE_TO_ONE_STUDENT_ID"),
      GET_HELPER_ONE_TO_ONE_STUDENT_ID: createStudentRoomAction("GET_HELPER_ONE_TO_ONE_STUDENT_ID"),
      GET_IS_TEACHER_ALLOW_STUDENT_SEE_HELPER_VIDEO: createStudentRoomAction("GET_IS_TEACHER_ALLOW_STUDENT_SEE_HELPER_VIDEO"),
      GET_IS_SHOW_CLASSMATE_VIDEOS: createStudentRoomAction("GET_IS_SHOW_CLASSMATE_VIDEOS"),
      GET_ROOM_MANAGER: createStudentRoomAction("GET_ROOM_MANAGER"),
    },
  },
  CLASS_TEACHING: {
    COMMITS: {
      SET_INITIAL_CLASS_DATA_SNAPSHOT: createClassTeachingAction("SET_INITIAL_CLASS_DATA_SNAPSHOT"),
      SET_IS_SOCKET_ATTEMPTED_RECONNECTING: createClassTeachingAction("SET_IS_SOCKET_ATTEMPTED_RECONNECTING"),
      SET_IS_INIT_CLASS_FAILED: createClassTeachingAction("SET_IS_INIT_CLASS_FAILED"),
      SET_INITIAL_ONE_TO_ONE_DATA: createClassTeachingAction("SET_INITIAL_ONE_TO_ONE_DATA"),
      SET_SESSION_INFO: createClassTeachingAction("SET_SESSION_INFO"),
      END_CLASS: createClassTeachingAction("END_CLASS"),
      SET_MODE: createClassTeachingAction("SET_MODE"),
      SET_INDEPENDENT_MODE_CURRENT_UNIT: createClassTeachingAction("SET_INDEPENDENT_MODE_CURRENT_UNIT"),
      SET_INDEPENDENT_MODE_CURRENT_LESSON: createClassTeachingAction("SET_INDEPENDENT_MODE_CURRENT_LESSON"),
      SET_ONE_TO_ONE_WITH_HELP_IGNORE_TEACHER_VOICE: createClassTeachingAction("SET_ONE_TO_ONE_WITH_HELP_IGNORE_TEACHER_VOICE"),
      SET_IS_RESTORING_SESSION_STATE: createClassTeachingAction("SET_IS_RESTORING_SESSION_STATE"),
    },
    GETTERS: {
      GET_INITIAL_CLASS_DATA_SNAPSHOT_EXPOSURE_ID: createClassTeachingAction("GET_INITIAL_CLASS_DATA_SNAPSHOT_EXPOSURE_ID"),
      GET_IS_SOCKET_ATTEMPTED_RECONNECTING: createClassTeachingAction("GET_IS_SOCKET_ATTEMPTED_RECONNECTING"),
      GET_IS_INIT_CLASS_FAILED: createClassTeachingAction("GET_IS_INIT_CLASS_FAILED"),
      GET_IS_INDEPENDENT_MODE_ENABLED: createClassTeachingAction("GET_IS_INDEPENDENT_MODE_ENABLED"),
      GET_IS_MUTE_TEACHER_AUDIO_WHEN_ONE_TO_ONE_WITH_HELPER: createClassTeachingAction("GET_IS_MUTE_TEACHER_AUDIO_WHEN_ONE_TO_ONE_WITH_HELPER"),
      GET_INITIAL_ONE_TO_ONE_DATA: createClassTeachingAction("GET_INITIAL_ONE_TO_ONE_DATA"),
      GET_SESSION_ID: createClassTeachingAction("GET_SESSION_ID"),
      GET_IS_INDEPENDENT_MODE: createClassTeachingAction("GET_IS_INDEPENDENT_MODE"),
      GET_INDEPENDENT_MODE_CURRENT_UNIT: createClassTeachingAction("GET_INDEPENDENT_MODE_CURRENT_UNIT"),
      GET_INDEPENDENT_MODE_CURRENT_LESSON: createClassTeachingAction("GET_INDEPENDENT_MODE_CURRENT_LESSON"),
      GET_ONE_TO_ONE_WITH_HELP_IGNORE_TEACHER_VOICE: createClassTeachingAction("GET_ONE_TO_ONE_WITH_HELP_IGNORE_TEACHER_VOICE"),
      GET_IS_RESTORING_SESSION_STATE: createClassTeachingAction("GET_IS_RESTORING_SESSION_STATE"),
    },
  },
  STUDENT_TEACHING: {
    COMMITS: {
      SET_CURRENT_INDEPENDENT_ITEM_ID: createStudentTeachingAction("SET_CURRENT_INDEPENDENT_ITEM_ID"),
    },
    GETTERS: {
      GET_CURRENT_INDEPENDENT_ITEM_ID: createStudentTeachingAction("GET_CURRENT_INDEPENDENT_ITEM_ID"),
    },
  },
  TEACHER_TEACHING: {
    DISPATCHES: {
      TOGGLE_INDEPENDENT_MODE: createTeacherTeachingAction("TOGGLE_INDEPENDENT_MODE"),
      HELPER_TOGGLE_INDEPENDENT_MODE: createTeacherTeachingAction("HELPER_TOGGLE_INDEPENDENT_MODE"),
    },
    COMMITS: {
      SET_INDEPENDENT_LIST_STUDENT_ITEMS: createTeacherTeachingAction("SET_INDEPENDENT_LIST_STUDENT_ITEMS"),
      UPDATE_INDEPENDENT_LIST_STUDENT_ITEMS: createTeacherTeachingAction("UPDATE_INDEPENDENT_LIST_STUDENT_ITEMS"),
    },
    GETTERS: {
      GET_INDEPENDENT_LIST_STUDENT_ITEMS: createTeacherTeachingAction("GET_INDEPENDENT_LIST_STUDENT_ITEMS"),
    },
  },
  TEACHER: {
    DISPATCHES: {
      SET_INFO: createTeacherAction("SET_INFO"),
      LOAD_ALL_CLASSES_SCHEDULES_ALL_SCHOOL: createTeacherAction("LOAD_ALL_CLASSES_SCHEDULES_ALL_SCHOOL"),
      LOAD_ALL_CLASSES_SCHEDULES: createTeacherAction("LOAD_ALL_CLASSES_SCHEDULES"),
      LOAD_SCHEDULES: createTeacherAction("LOAD_SCHEDULES"),
      LOAD_ALL_SCHEDULES: createTeacherAction("LOAD_ALL_SCHEDULES"),
      SKIP_SCHEDULE: createTeacherAction("SKIP_SCHEDULE"),
      CREATE_SCHEDULE: createTeacherAction("CREATE_SCHEDULE"),
      UPDATE_SCHEDULE: createTeacherAction("UPDATE_SCHEDULE"),
      DELETE_SCHEDULE: createTeacherAction("DELETE_SCHEDULE"),
      LOAD_ACCESSIBLE_SCHOOLS: createTeacherAction("LOAD_ACCESSIBLE_SCHOOLS"),
      SET_ACCEPT_POLICY: createTeacherAction("SET_ACCEPT_POLICY"),
      SET_CLASS_GROUP: createTeacherAction("SET_CLASS_GROUP"),
      SET_CURRENT_SCHOOL: createTeacherAction("SET_CURRENT_SCHOOL"),
      UPDATE_ONGOING_SESSION: createTeacherAction("UPDATE_ONGOING_SESSION"),
    },
    COMMITS: {
      SET_SCHOOLS: createTeacherAction("SET_SCHOOLS"),
      SET_CLASSES: createTeacherAction("SET_CLASSES"),
      SET_CLASSES_SCHEDULES: createTeacherAction("SET_CLASSES_SCHEDULES"),
      SET_CLASSES_SCHEDULES_ALL_SCHOOL: createTeacherAction("SET_CLASSES_SCHEDULES_ALL_SCHOOL"),
      CLEAR_CALENDAR_SCHEDULE: createTeacherAction("CLEAR_CALENDAR_SCHEDULE"),
      CLEAR_ALL_CLASSES_SCHEDULES_ALL_SCHOOL: createTeacherAction("CLEAR_ALL_CLASSES_SCHEDULES_ALL_SCHOOL"),
      SET_CALENDAR_SCHEDULES: createTeacherAction("SET_CALENDAR_SCHEDULES"),
      SET_INFO: createTeacherAction("SET_INFO"),
      SET_ACCEPT_POLICY: createTeacherAction("SET_ACCEPT_POLICY"),
      UPDATE_CALENDAR_SCHEDULE: createTeacherAction("UPDATE_CALENDAR_SCHEDULE"),
      SET_CURRENT_SCHOOL: createTeacherAction("SET_CURRENT_SCHOOL"),
      SET_CLASS_SET_UP_STUDENTS: createTeacherAction("SET_CLASS_SET_UP_STUDENTS"),
      SET_CLASS_GROUP: createTeacherAction("SET_CLASS_GROUP"),
      SET_CALENDAR_FILTER: createTeacherAction("SET_CALENDAR_FILTER"),
      SET_IS_HELPER: createTeacherAction("SET_IS_HELPER"),
      SET_IGNORE_HELPER_REQUEST_JOIN_CLASS: createTeacherAction("SET_IGNORE_HELPER_REQUEST_JOIN_CLASS"),
      SET_IS_HELPER_TAKING_OVER: createTeacherAction("SET_IS_HELPER_TAKING_OVER"),
      SET_ONGOING_SESSION: createTeacherAction("SET_ONGOING_SESSION"),
    },
    GETTERS: {
      GET_CLASSES_SCHEDULES: createTeacherAction("GET_CLASSES_SCHEDULES"),
      GET_CLASSES_SCHEDULES_ALL_SCHOOL: createTeacherAction("GET_CLASSES_SCHEDULES_ALL_SCHOOL"),
      GET_SCHOOLS: createTeacherAction("GET_SCHOOLS"),
      GET_ACCEPT_POLICY: createTeacherAction("GET_ACCEPT_POLICY"),
      GET_CALENDAR_SCHEDULES: createTeacherAction("GET_CALENDAR_SCHEDULES"),
      GET_CURRENT_SCHOOL_ID: createTeacherAction("GET_CURRENT_SCHOOL_ID"),
      GET_CLASS_SET_UP_STUDENTS: createTeacherAction("GET_CLASS_SET_UP_STUDENTS"),
      GET_CLASS_GROUP: createTeacherAction("GET_CLASS_GROUP"),
      GET_CALENDAR_FILTER: createTeacherAction("GET_CALENDAR_FILTER"),
      GET_IS_HELPER: createTeacherAction("GET_IS_HELPER"),
      GET_IGNORE_HELPER_REQUEST_JOIN_CLASS: createTeacherAction("GET_IGNORE_HELPER_REQUEST_JOIN_CLASS"),
      GET_IS_HELPER_TAKING_OVER: createTeacherAction("GET_IS_HELPER_TAKING_OVER"),
      GET_ONGOING_SESSION: createTeacherAction("GET_ONGOING_SESSION"),
    },
  },
  CALLING: {
    DISPATCHES: {
      CALLING_UPDATE_DEVICES: createCallingAction("CALLING_UPDATE_DEVICES"),
      RE_REGISTER_HOT_PLUGGING_DEVICES_EVENT_LISTENER: createCallingAction("RE_REGISTER_HOT_PLUGGING_DEVICES_EVENT_LISTENER"),
      POST_LOCAL_USER_RECONNECTED: createCallingAction("POST_LOCAL_USER_RECONNECTED"),
    },
    COMMITS: {
      SET_VIDEO_USER_IDS: createCallingAction("SET_VIDEO_USER_IDS"),
      SET_AUDIO_USER_IDS: createCallingAction("SET_AUDIO_USER_IDS"),
      SET_IS_JOINED_SESSION: createCallingAction("SET_IS_JOINED_SESSION"),
      SET_VIDEO_CALLING_QUALITY: createCallingAction("SET_VIDEO_CALLING_QUALITY"),
      RESET_REMOTE_USERS_STATE: createCallingAction("RESET_REMOTE_USERS_STATE"),
      SET_CALLING_USER_IDS: createCallingAction("SET_CALLING_USER_IDS"),
      RESET_STATE: createCallingAction("RESET_STATE"),
      SET_ENABLED_AGORA_NOISE_CANCELLING: createCallingAction("SET_ENABLED_AGORA_NOISE_CANCELLING"),
      SET_POSTLOCAL_USER_RECONNECTED_INTERVAL_ID: createCallingAction("SET_POSTLOCAL_USER_RECONNECTED_INTERVAL_ID"),
      SET_CAMERA_ERROR: createCallingAction("SET_CAMERA_ERROR"),
      SET_MICROPHONE_ERROR: createCallingAction("SET_MICROPHONE_ERROR"),
      SET_SPEAKER_ERROR: createCallingAction("SET_SPEAKER_ERROR"),
    },
    GETTERS: {
      GET_VIDEO_USER_IDS: createCallingAction("GET_VIDEO_USER_IDS"),
      GET_AUDIO_USER_IDS: createCallingAction("GET_AUDIO_USER_IDS"),
      GET_IS_JOINED_SESSION: createCallingAction("GET_IS_JOINED_SESSION"),
      GET_VIDEO_CALLING_QUALITY: createCallingAction("GET_VIDEO_CALLING_QUALITY"),
      GET_CALLING_USER_IDS: createCallingAction("GET_CALLING_USER_IDS"),
      GET_ENABLED_AGORA_NOISE_CANCELLING: createCallingAction("GET_ENABLED_AGORA_NOISE_CANCELLING"),
      GET_IS_CALLING_HAS_USER_BY_ID: createCallingAction("GET_IS_CALLING_HAS_USER_BY_ID"),
      GET_POSTLOCAL_USER_RECONNECTED_INTERVAL_ID: createCallingAction("GET_POSTLOCAL_USER_RECONNECTED_INTERVAL_ID"),
      GET_CAMERA_ERROR: createCallingAction("GET_CAMERA_ERROR"),
      GET_MICROPHONE_ERROR: createCallingAction("GET_MICROPHONE_ERROR"),
    },
  },
  ANNOTATION_V2: {
    DISPATCHES: {
      ADD_OBJECT: createAnnotationV2Action("ADD_OBJECT"),
      UPDATE_OBJECT: createAnnotationV2Action("UPDATE_OBJECT"),
      REMOVE_OBJECT: createAnnotationV2Action("REMOVE_OBJECT"),
      SET_OBJECTS: createAnnotationV2Action("SET_OBJECTS"),
      CLEAR_ALL_OBJECTS: createAnnotationV2Action("CLEAR_ALL_OBJECTS"),
      SET_REALTIME_PENCIL_PATH: createAnnotationV2Action("SET_REALTIME_PENCIL_PATH"),
      SET_REALTIME_LASER_PATH: createAnnotationV2Action("SET_REALTIME_LASER_PATH"),
      REMOVE_REALTIME_PENCIL_PATH: createAnnotationV2Action("REMOVE_REALTIME_PENCIL_PATH"),
      CLEAR_REALTIME_PENCIL_PATHS: createAnnotationV2Action("CLEAR_REALTIME_PENCIL_PATHS"),
    },
    COMMITS: {
      RESET_STATE: createAnnotationV2Action("RESET_STATE"),
      SET_ANNOTATION_MODE: createAnnotationV2Action("SET_ANNOTATION_MODE"),
      ADD_OBJECT: createAnnotationV2Action("ADD_OBJECT"),
      UPDATE_OBJECT: createAnnotationV2Action("UPDATE_OBJECT"),
      REMOVE_OBJECT: createAnnotationV2Action("REMOVE_OBJECT"),
      SET_OBJECTS: createAnnotationV2Action("SET_OBJECTS"),
      CLEAR_ALL_OBJECTS: createAnnotationV2Action("CLEAR_ALL_OBJECTS"),
      SET_IMAGE_DIMENSIONS: createAnnotationV2Action("SET_IMAGE_DIMENSIONS"),
      SET_REALTIME_PENCIL_PATH: createAnnotationV2Action("SET_REALTIME_PENCIL_PATH"),
      CLEAR_REALTIME_PENCIL_PATHS: createAnnotationV2Action("CLEAR_REALTIME_PENCIL_PATHS"),
      REMOVE_REALTIME_PENCIL_PATH: createAnnotationV2Action("REMOVE_REALTIME_PENCIL_PATH"),
      SET_REALTIME_LASER_PATH: createAnnotationV2Action("SET_REALTIME_LASER_PATH"),
      SET_NEXT_OBJECT_TOBE_UPDATED: createAnnotationV2Action("SET_NEXT_OBJECT_TOBE_UPDATED"),
    },
    GETTERS: {
      IS_POINTER_MODE: createAnnotationV2Action("IS_POINTER_MODE"),
      GET_CURRENT_OBJECTS: createAnnotationV2Action("GET_CURRENT_OBJECTS"),
      GET_PATHS: createAnnotationV2Action("GET_PATHS"),
      GET_SHAPES: createAnnotationV2Action("GET_SHAPES"),
      GET_TEXTBOXES: createAnnotationV2Action("GET_TEXTBOXES"),
      GET_TEACHER_ONLY_OBJECTS: createAnnotationV2Action("GET_TEACHER_ONLY_OBJECTS"),
      GET_OBJECT_BY_ID: createAnnotationV2Action("GET_OBJECT_BY_ID"),
      GET_OBJECTS_BY_USER: createAnnotationV2Action("GET_OBJECTS_BY_USER"),
      OBJECT_COUNT: createAnnotationV2Action("OBJECT_COUNT"),
      HAS_OBJECTS: createAnnotationV2Action("HAS_OBJECTS"),
      GET_REALTIME_PENCIL_PATHS: createAnnotationV2Action("GET_REALTIME_PENCIL_PATHS"),
      GET_REALTIME_LASER_PATHS: createAnnotationV2Action("GET_REALTIME_LASER_PATHS"),
      GET_IMAGE_WIDTH: createAnnotationV2Action("GET_IMAGE_WIDTH"),
      GET_IMAGE_HEIGHT: createAnnotationV2Action("GET_IMAGE_HEIGHT"),
      EXPORT_FOR_BACKEND: createAnnotationV2Action("EXPORT_FOR_BACKEND"),
      GET_NEXT_OBJECT_TOBE_UPDATED: createAnnotationV2Action("GET_NEXT_OBJECT_TOBE_UPDATED"),
    },
  },
  MODAL: {
    DISPATCHES: {
      INFORM_DEVICE_WARNING: createModalAction("INFORM_DEVICE_WARNING"),
      WARNING_IDP_MODE_BEFORE_EXECUTE: createModalAction("WARNING_IDP_MODE_BEFORE_EXECUTE"),
      CONFIRM_REPLACEMENT_SESSION: createModalAction("CONFIRM_REPLACEMENT_SESSION"),
    },
    COMMITS: {
      OPEN: createModalAction("OPEN"),
      CLOSE: createModalAction("CLOSE"),
      SET_PROPS: createModalAction("SET_PROPS"),
      SET_IS_SUBMITTING: createModalAction("SET_IS_SUBMITTING"),
    },
    GETTERS: {
      NAME: createModalAction("NAME"),
      GET_IS_OPEN: createModalAction("GET_IS_OPEN"),
      GET_TITLE: createModalAction("GET_TITLE"),
      GET_ACTIONS: createModalAction("GET_ACTIONS"),
      GET_VIEW: createModalAction("GET_VIEW"),
      GET_PROPS: createModalAction("GET_PROPS"),
      GET_IS_SUBMITTING: createModalAction("GET_IS_SUBMITTING"),
    },
  },
  DEVICE: {
    COMMITS: {
      TOGGLE_DEVICE_SETTING_MODAL: createDeviceAction("TOGGLE_DEVICE_SETTING_MODAL"),
    },
    GETTERS: {
      IS_SETTING_DEVICE_MODAL_OPEN: createDeviceAction("IS_SETTING_DEVICE_MODAL_OPEN"),
      SETTING_DEVICE_MODAL_VIEW: createDeviceAction("SETTING_DEVICE_MODAL_VIEW"),
    },
  },
  PARENT: {
    DISPATCHES: {
      SET_INFO: createParentAction("SET_INFO"),
      LOAD_CHILDREN: createParentAction("LOAD_CHILDREN"),
      SET_ACCEPT_POLICY: createParentAction("SET_ACCEPT_POLICY"),
    },
    COMMITS: {
      SET_CHILDREN: createParentAction("SET_CHILDREN"),
      SET_INFO: createParentAction("SET_INFO"),
      SET_ACCEPT_POLICY: createParentAction("SET_ACCEPT_POLICY"),
    },
    GETTERS: {
      GET_CHILDREN: createParentAction("GET_CHILDREN"),
      GET_ACCEPT_POLICY: createParentAction("GET_ACCEPT_POLICY"),
    },
  },
  SPIN: {
    COMMITS: {
      SET_MASK_MAIN: createSpinAction("SET_MASK_MAIN"),
      SET_MASK_GRAND_ACCESS: createSpinAction("SET_MASK_GRAND_ACCESS"),
      SET_SPLASH: createSpinAction("SET_SPLASH"),
    },
    GETTERS: {
      GET_MASK_GRAND_ACCESS: createSpinAction("GET_MASK_GRAND_ACCESS"),
      GET_SPLASH: createSpinAction("GET_SPLASH"),
    },
  },
  WHITE_BOARD: {
    COMMITS: {
      RESET_STATE: createWhiteBoardAction("RESET_STATE"),
      SET_MEDIA_STATE: createWhiteBoardAction("SET_MEDIA_STATE"),
      SET_SHOULD_FETCH_MEDIA_STATE: createWhiteBoardAction("SET_SHOULD_FETCH_MEDIA_STATE"),
    },
    GETTERS: {
      GET_WHITE_BOARD_MEDIA_STATE: createWhiteBoardAction("GET_WHITE_BOARD_MEDIA_STATE"),
      GET_SHOULD_FETCH_MEDIA_STATE: createWhiteBoardAction("GET_SHOULD_FETCH_MEDIA_STATE"),
    },
  },
  TEAMS: {
    DISPATCHES: {
      GET_ALL_STUDENTS: createTeamsAction("GET_ALL_STUDENTS"),
      GET_TEAMS: createTeamsAction("GET_TEAMS"),
      UPDATE_TEAMS_ASYNC: createTeamsAction("UPDATE_TEAMS_ASYNC"),
      TOGGLE_TEAM_MODE_ASYNC: createTeamsAction("TOGGLE_TEAM_MODE_ASYNC"),
      ON_SETTING_MODAL_SAVE: createTeamsAction("ON_SETTING_MODAL_SAVE"),
      ON_SETTING_MODAL_CANCEL: createTeamsAction("ON_SETTING_MODAL_CANCEL"),
    },
    COMMITS: {
      SET_ALL_STUDENTS: createTeamsAction("SET_ALL_STUDENTS"),
      SET_TEAMS: createTeamsAction("SET_TEAMS"),
      SET_IS_TEAM_MODE: createTeamsAction("SET_IS_TEAM_MODE"),
      SET_IS_LOADING: createTeamsAction("SET_IS_LOADING"),
      SET_IS_SETTING_MODAL_OPEN: createTeamsAction("SET_IS_SETTING_MODAL_OPEN"),
      SET_IS_ENABLE_TEAM_MODE_ON_SAVE: createTeamsAction("SET_IS_ENABLE_TEAM_MODE_ON_SAVE"),
      SET_TEAMS_REVISION: createTeamsAction("SET_TEAMS_REVISION"),
    },
    GETTERS: {
      GET_TEAMS: createTeamsAction("GET_TEAMS"),
      GET_ALL_STUDENTS: createTeamsAction("GET_ALL_STUDENTS"),
      GET_IS_TEAM_MODE: createTeamsAction("GET_IS_TEAM_MODE"),
      GET_IS_LOADING: createTeamsAction("GET_IS_LOADING"),
      GET_AVATAR_URL_BY_ID: createTeamsAction("GET_AVATAR_URL_BY_ID"),
      GET_IS_SETTING_MODAL_OPEN: createTeamsAction("GET_IS_SETTING_MODAL_OPEN"),
      GET_IS_ENABLE_TEAM_MODE_ON_SAVE: createTeamsAction("GET_IS_ENABLE_TEAM_MODE_ON_SAVE"),
      GET_TEAMS_REVISION: createTeamsAction("GET_TEAMS_REVISION"),
    },
  },
  APP: {
    DISPATCHES: {
      SET_TOAST: createAppAction("SET_TOAST"),
      SET_LAYOUT: createAppAction("SET_LAYOUT"),
      SET_APP_VIEW: createAppAction("SET_APP_VIEW"),
      SET_CAMERA_DEVICE_ID: createAppAction("SET_CAMERA_DEVICE_ID"),
      SET_MICROPHONE_DEVICE_ID: createAppAction("SET_MICROPHONE_DEVICE_ID"),
      SET_SPEAKER_DEVICE_ID: createAppAction("SET_SPEAKER_DEVICE_ID"),
      SET_USER_ROLE_BY_VIEW: createAppAction("SET_USER_ROLE_BY_VIEW"),
      SET_LOW_BAND_WIDTH: createAppAction("SET_LOW_BAND_WIDTH"),
    },
    COMMITS: {
      SET_TOAST: createAppAction("SET_TOAST"),
      SET_LAYOUT: createAppAction("SET_LAYOUT"),
      SET_APP_VIEW: createAppAction("SET_APP_VIEW"),
      SET_CAMERA_DEVICE_ID: createAppAction("SET_CAMERA_DEVICE_ID"),
      SET_MICROPHONE_DEVICE_ID: createAppAction("SET_MICROPHONE_DEVICE_ID"),
      SET_SPEAKER_DEVICE_ID: createAppAction("SET_SPEAKER_DEVICE_ID"),
      SET_USER_ROLE_BY_VIEW: createAppAction("SET_USER_ROLE_BY_VIEW"),
      SET_LOW_BAND_WIDTH: createAppAction("SET_LOW_BAND_WIDTH"),
      SET_HAVE_PERMISSION_CAMERA: createAppAction("SET_HAVE_PERMISSION_CAMERA"),
      SET_HAVE_PERMISSION_MICROPHONE: createAppAction("SET_HAVE_PERMISSION_MICROPHONE"),
    },
    GETTERS: {
      GET_APP_VIEW: createAppAction("GET_APP_VIEW"),
      GET_APP_TOAST: createAppAction("GET_APP_TOAST"),
      GET_CAMERA_DEVICE_ID: createAppAction("GET_CAMERA_DEVICE_ID"),
      GET_MICROPHONE_DEVICE_ID: createAppAction("GET_MICROPHONE_DEVICE_ID"),
      GET_SPEAKER_DEVICE_ID: createAppAction("GET_SPEAKER_DEVICE_ID"),
      GET_USER_ROLE: createAppAction("GET_USER_ROLE"),
      GET_LOW_BAND_WIDTH: createAppAction("GET_LOW_BAND_WIDTH"),
      GET_REPLACED_CLASS_ID: createAppAction("GET_REPLACED_CLASS_ID"),
      GET_HAVE_PERMISSION_CAMERA: createAppAction("GET_HAVE_PERMISSION_CAMERA"),
      GET_HAVE_PERMISSION_MICROPHONE: createAppAction("GET_HAVE_PERMISSION_MICROPHONE"),
      GET_HAVE_PERMISSION_SPEAKER: createAppAction("GET_HAVE_PERMISSION_SPEAKER"),
    },
  },
  AUTH: {
    COMMITS: {
      SET_LOGIN_INFO: createAuthAction("SET_LOGIN_INFO"),
      UPDATE_NEW_TOKEN: createAuthAction("UPDATE_NEW_TOKEN"),
    },
    GETTERS: {
      GET_IS_LOGGED_IN: createAuthAction("GET_IS_LOGGED_IN"),
      GET_IS_ONLY_PARENT: createAuthAction("GET_IS_ONLY_PARENT"),
      GET_IS_ONLY_TEACHER: createAuthAction("GET_IS_ONLY_TEACHER"),
      GET_IS_PARENT_AND_TEACHER: createAuthAction("GET_IS_PARENT_AND_TEACHER"),
      GET_LOGIN_INFO: createAuthAction("GET_LOGIN_INFO"),
      GET_USERNAME: createAuthAction("GET_USERNAME"),
      GET_IS_TEACHER: createAuthAction("GET_IS_TEACHER"),
      GET_IS_PARENT: createAuthAction("GET_IS_PARENT"),
      GET_USER_ID: createAuthAction("GET_USER_ID"),
      GET_USER_COUNTRY_CODE: createAuthAction("GET_USER_COUNTRY_CODE"),
    },
  },
};
